import { Controller, useFormContext } from "react-hook-form";
import { SimpleSelect } from "../../../../common/components/Select/SimpleSelect";
import { type SASearchFormFields } from "../SalesPartnerSearchBox";
import { useTranslation } from "react-i18next";
import { MenuItem, PopoverPaper, Select } from "@mui/material";
import { fontSize, margin, styled } from "@mui/system";
import { C_SURFACE } from "~/common/components/icon/Colors";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { trackPartnerFinderSearch } from "~/feature/Analytics/ga/salesPartner";
import { ELEVATION_2 } from "~/common/components/icon/Elevation";

interface Props {
  className?: string;
  disabled?: boolean;
}

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: C_SURFACE,
  ...getFontStyle(FontSize.BodyLarge),
}));

const radiusMenuProps = {
  PaperProps: {
    sx: {
      mt: "8px",
      boxShadow: ELEVATION_2,
      "& ul": {
        backgroundColor: C_SURFACE,
      },
      "& li": {
        ...getFontStyle(FontSize.BodyMedium),
      },
    },
  },
};

export const RadiusSelect = ({ className, disabled }: Props) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext<SASearchFormFields>();
  const useImperial = window.SCHA.config.isSPFImperial;

  const radiusDropdownMetricValues = [
    { label: "10 km", value: 10 },
    { label: "20 km", value: 20 },
    { label: "50 km", value: 50 },
    { label: "100 km", value: 100 },
    { label: "500 km", value: 500 },
    { label: t("salesPartner.searchCard.radiusLabel.unlimited"), value: -1 },
  ];

  const radiusDropdownImperialValues = [
    { label: "10 mi", value: 10 },
    { label: "20 mi", value: 20 },
    { label: "50 mi", value: 50 },
    { label: "100 mi", value: 100 },
    { label: "500 mi", value: 500 },
    { label: t("salesPartner.searchCard.radiusLabel.unlimited"), value: -1 },
  ];

  const radiusDropdownValues = useImperial ? radiusDropdownImperialValues : radiusDropdownMetricValues;

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  return (
    <Controller
      render={({ field: { value, onChange, onBlur } }) => (
        <Select
          label={t("salesPartner.searchCard.label.radius")}
          onBlur={onBlur}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            trackPartnerFinderSearch(getValues(), "Filter Applied", "Click", undefined, "Radius");
          }}
          disabled={disabled}
          sx={{
            width: isMobile ? "100%" : "180px",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          MenuProps={radiusMenuProps}
        >
          {radiusDropdownValues.map((radius) => (
            <StyledMenuItem value={radius.value}>{radius.label}</StyledMenuItem>
          ))}
        </Select>
      )}
      name={"radius"}
      rules={{ required: true }}
      defaultValue={100}
      control={control}
    />
  );
};
